import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import LpgCancelOutline from "@/icons/Lpg/CancelOutline";

import { Portal } from "../Portal";
import { Button } from "../Button";
import { A11yText } from "../A11yText";
import { Icon } from "../Icon";
import { useFocusTrap } from "../hooks/useFocusTrap";

import YoutubeVideo from "./YoutubeVideo";
import styles from "./ModalVideo.scss";

function ModalVideoPortal({
  node,
  id,
  isOpened,
  closeText,
  close,
  portalless,
  loadYtVideo,
  children,
  ...otherProps
}) {
  const [isVisible, setIsVisible] = useState(isOpened);
  const [play, setPlay] = useState(false);
  const [currentTrigger, setCurrentTrigger] = useState(null);
  const modalRef = useRef();
  // References dummy button that handles keyboard controls of yt video through yt api
  const ytControlButtonRef = useRef();
  // Reference to youtube video in order to access youtube api functions like playVideo()
  const videoRef = useRef();
  const { handleTabBehaviour } = useFocusTrap(modalRef, videoRef.current);

  useEffect(() => {
    setIsVisible(isOpened);
    if (isOpened) {
      setCurrentTrigger(document.activeElement);
      setPlay(true);
    }
  }, [isOpened]);

  function closePortal(e) {
    e.stopPropagation();
    setPlay(false);
    if (close) {
      close();
    }
    if (currentTrigger) {
      currentTrigger.focus();
    }
  }

  function handleYtVideoControls(e) {
    if (!videoRef.current) return;

    const keyCode = e.which;
    // prevent anything default behaviour for anything but tab
    if (keyCode !== 9) {
      e.preventDefault();
    }

    // Implements most common default Youtube Video keyboard controls
    switch (keyCode) {
      // ESC, Return - Close Modal
      case 9:
        handleTabBehaviour(e);
        break;
      case 13:
      case 27:
        closePortal(e);
        break;
      // Spacebar, k - Play/pause
      case 32:
      case 75:
        if (isVisible) {
          if (videoRef.current.getPlayerState() === 1) {
            setPlay(false);
          } else if (videoRef.current.getPlayerState() === 2) {
            setPlay(true);
          }
        }
        break;
      // Left Arrow - Go back 5s
      case 37:
        videoRef.current.seekTo(videoRef.current.getCurrentTime() - 5);
        break;
      // Up Arrow - Volume up 5%
      case 38:
        if (videoRef.current.isMuted()) {
          videoRef.current.unMute();
        }
        videoRef.current.setVolume(videoRef.current.getVolume() + 5);
        break;
      // Right Arrow - G o forward 5s
      case 39:
        videoRef.current.seekTo(videoRef.current.getCurrentTime() + 5);
        break;
      // Down Arrow - Volume down 5%
      case 40:
        videoRef.current.setVolume(videoRef.current.getVolume() + -5);
        break;
      // m - Mute
      case 77:
        if (videoRef.current.isMuted()) {
          videoRef.current.unMute();
        } else {
          videoRef.current.mute();
        }
        break;
      default:
        break;
    }
  }

  const PortalTag = portalless ? "div" : Portal;

  return (
    <PortalTag node={node}>
      <div
        className={`${styles.container} ${isVisible ? "is-visible" : ""}`}
        role="button"
        tabIndex={0}
        onKeyDown={null}
        onClick={closePortal}
      >
        <div
          className={styles.content}
          aria-modal="true"
          role="dialog"
          ref={modalRef}
          {...otherProps}
        >
          <div className={styles["video-button-wrap"]}>
            <Button
              variants={[Button.Variant.clean, Button.Variant.reactangle]}
              type="button"
              tabIndex={0}
              onClick={closePortal}
              className={styles["close-button"]}
            >
              <Icon
                icon={LpgCancelOutline}
                color="#FFFFFF"
                size={Icon.Size.medium}
              />
              <A11yText>{closeText}</A11yText>
            </Button>
            {loadYtVideo ? (
              <>
                <button
                  aria-label="Video Controls"
                  type="button"
                  ref={ytControlButtonRef}
                  onKeyDown={handleYtVideoControls}
                  className={styles["yt-controls"]}
                />

                <YoutubeVideo
                  ref={videoRef}
                  id={id}
                  play={play}
                  onPlay={() =>
                    setTimeout(() => ytControlButtonRef.current.focus(), 100)
                  }
                >
                  {children}
                </YoutubeVideo>
              </>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </PortalTag>
  );
}

ModalVideoPortal.propTypes = {
  node:
    typeof Element !== "undefined"
      ? PropTypes.instanceOf(Element).isRequired
      : PropTypes.number,
  id: PropTypes.string.isRequired,
  isOpened: PropTypes.bool.isRequired,
  closeText: PropTypes.string.isRequired,
  close: PropTypes.func,
  portalless: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  loadYtVideo: PropTypes.bool,
};

ModalVideoPortal.defaultProps = {
  node: null,
  close: null,
  portalless: false,
  children: null,
  loadYtVideo: false,
};

ModalVideoPortal.displayName = "ModalVideoPortal";

export default ModalVideoPortal;
